import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, TemplateRef } from '@angular/core';
import { Marked } from 'marked';

import { AgentMessageActionsComponent } from 'AgentApp/ts/features/agent/components/agent-message-actions/agent-message-actions.component';

import {
    AGENT_MARKDOWN_RENDERER_OPTIONS,
    INCOMPLETE_MARKDOWN_LINK_REGEX,
} from 'AgentApp/ts/constants/agent-markdown.constants';
import { AgentDisplayAction } from 'AgentApp/ts/interfaces/agent-message.interface';

import { AgentDownloadLinkComponent } from 'AgentApp/ts/features/agent/components/agent-download-link/agent-download-link.component';
import { AgentLinkInterceptDirective } from 'AgentApp/ts/features/agent/directives/agent-link-intercept.directive';

const marked = new Marked({ renderer: { ...AGENT_MARKDOWN_RENDERER_OPTIONS } });

/**
 * Agent Response Message
 *
 * Renders an assistant message — the markdown text plus any display actions — for both the agent page and the
 * AI side panel. The two surfaces differ only presentationally, controlled by inputs:
 *   - `chartActionTemplate`: a template the agent page supplies to render chart display actions as inline chart
 *     cards. The side panel supplies none, so chart actions fall through to a plain link — and, crucially, the
 *     heavy chart components are never imported here, keeping them out of the site-wide side-panel bundle.
 *   - `includeResponseProfileImage`: wraps the message in the agent page's white response box.
 *   - `displayAsCard`: wraps the message in a padded white card (side panel).
 *
 * A single message can contain more than one display action, so the dispatch lives here. Chart actions are
 * projected via `chartActionTemplate` (the caller owns the chart components); add new chart actions there.
 */
@Component({
    selector: 'ycn-agent-response-message',
    templateUrl: './agent-response-message.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, AgentDownloadLinkComponent, AgentLinkInterceptDirective, AgentMessageActionsComponent],
})
export class AgentResponseMessageComponent {
    protected readonly messageText = input.required<string>();

    protected readonly includeResponseProfileImage = input(false);

    protected readonly chartActionTemplate = input<TemplateRef<{ $implicit: AgentDisplayAction }> | null>(null);

    protected readonly displayAsCard = input(false);

    protected readonly displayActions = input<AgentDisplayAction[]>([]);

    protected readonly deleteChat = output<void>();

    protected readonly messageHtml = computed(() => {
        const cleanedContent = this.messageText().replace(INCOMPLETE_MARKDOWN_LINK_REGEX, '*Creating Link...*');
        return marked.parse(cleanedContent) as string;
    });
}
